import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from 'app/api'
import type { Options } from 'app/dataProviders/useCommonDataProvider'

// Every parameter change starts a request; only the latest response updates the UI.
export const useSurveyData = <Data, Params>(options: Options<Data, Params>) => {
  const optionsRef = useRef(options)
  optionsRef.current = options
  const generation = useRef(0)
  const mounted = useRef(true)
  const [data, setData] = useState(options.defaultState)
  const [loading, setLoading] = useState(options.initialLoading !== false)
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const paramsKey = JSON.stringify(
    options.convertions.convertParams?.(options.params as Params) || options.params,
  )

  const request = useCallback(async (overrides?: Partial<Params>) => {
    const current = optionsRef.current
    const params = { ...current.params, ...overrides } as Params
    const requestKey = JSON.stringify(
      current.convertions.convertParams?.(current.params as Params) || current.params,
    )
    const id = ++generation.current
    setLoading(true)
    try {
      const result = await api.request(current.requestValue, {
        params,
        error: current.error,
        convertions: current.convertions,
      })
      if (mounted.current && id === generation.current) {
        if (result != null) setData(result)
        setLoadedKey(requestKey)
      }
      return result
    } finally {
      if (mounted.current && id === generation.current) setLoading(false)
    }
  }, [])
  const requestRef = useRef(request)
  requestRef.current = request

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      generation.current += 1
    }
  }, [])

  useEffect(() => {
    void request()
  }, [paramsKey, request])

  return { data, loading: loading || loadedKey !== paramsKey, request, requestRef, setData }
}
